// src/LiveNow.js
// Блок «Сейчас»: какой урок идёт, в каком кабинете какой класс и у кого,
// сколько осталось до конца урока или перемены. Считается в браузере по
// московскому времени, данные — /api/timetable/ (обновляем раз в 10 минут).
import React, { useEffect, useState } from 'react';
import { api } from './api';
import { dayStatus, formatCountdown, isTimetableAvailable, moscowNow, roomRows } from './timetableUtils';
import './LiveNow.css';

const REFRESH_MS = 10 * 60 * 1000;
// Свёрнутая таблица показывает первые кабинеты, остальное — по кнопке
const COLLAPSED_ROWS = 5;
const EXPANDED_KEY = 'eu_live_expanded';

// Выбор «развернуть/свернуть» запоминаем в браузере; хранилище может быть недоступно
function useExpanded() {
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem(EXPANDED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggle = () => {
    setExpanded((value) => {
      try {
        localStorage.setItem(EXPANDED_KEY, value ? '0' : '1');
      } catch {
        // не запомним — не страшно
      }
      return !value;
    });
  };
  return [expanded, toggle];
}

// enabled = false — данные уже загрузил родитель (страница «Расписание»)
export function useTimetable(enabled = true) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const load = () => api.getTimetable().then((d) => alive && setData(d), () => {});
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [enabled]);
  return data;
}

function useNow() {
  const [now, setNow] = useState(() => moscowNow());
  useEffect(() => {
    const timer = setInterval(() => setNow(moscowNow()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

function Countdown({ seconds, label }) {
  const { words, clock } = formatCountdown(seconds);
  return (
    <div className="live-countdown">
      <span className="live-countdown-label">{label}</span>
      <span className="live-countdown-words">{words}</span>
      <span className="live-countdown-clock">{clock}</span>
    </div>
  );
}

export default function LiveNow({ timetable: given }) {
  const own = useTimetable(!given);
  const timetable = given || own;
  const now = useNow();
  const [expanded, toggleExpanded] = useExpanded();

  // Нет расписания из МЭШ, оно скрыто администратором или устарело —
  // блока нет ни у кого, в том числе у администратора
  if (!timetable || !timetable.lessons || timetable.lessons.length === 0) return null;
  if (!isTimetableAvailable(timetable)) return null;

  const status = dayStatus(timetable.lessons, now);
  let title;
  let countdown = null;
  let rows = [];
  let rowsTitle = null;

  if (status.state === 'lesson') {
    title = `Идёт ${status.number}-й урок`;
    countdown = <Countdown seconds={status.left} label="до конца урока" />;
    rows = status.current;
  } else if (status.state === 'break') {
    title = 'Перемена';
    countdown = <Countdown seconds={status.left} label="до конца перемены" />;
    rows = status.next;
    rowsTitle = `После перемены — ${status.number}-й урок`;
  } else if (status.state === 'before') {
    title = 'Уроки ещё не начались';
    countdown = <Countdown seconds={status.left} label="до первого урока" />;
    rows = status.next;
    rowsTitle = `${status.number}-й урок`;
  } else if (status.state === 'over') {
    title = 'Уроки на сегодня закончились';
  } else {
    title = 'Сегодня уроков нет';
  }

  return (
    <section className={`live-now live-${status.state}`} aria-live="polite">
      <div className="live-head">
        <div className="live-title">
          <span className="live-dot" aria-hidden="true"></span>
          <span className="live-label">Сейчас</span>
          <span className="live-state">{title}</span>
        </div>
        {countdown}
      </div>

      {rows.length > 0 && (
        <>
          {rowsTitle && <div className="live-rows-title">{rowsTitle}</div>}
          <div className="live-table" role="table">
            <div className="live-row live-row-head" role="row">
              <span role="columnheader">Кабинет</span>
              <span role="columnheader">Класс</span>
              <span role="columnheader">Урок</span>
              <span role="columnheader">Учитель</span>
            </div>
            {roomRows(rows).slice(0, expanded ? undefined : COLLAPSED_ROWS).map((l, i) => (
              <div className="live-row" role="row" key={`${l.class_name}-${l.subject}-${l.room}-${i}`}>
                <span className="live-room" role="cell">{l.room || '—'}</span>
                <span className="live-class" role="cell">{l.class_name}</span>
                <span className="live-subject" role="cell">{l.subject}</span>
                <span className="live-teacher" role="cell">{l.teacher || '—'}</span>
              </div>
            ))}
          </div>
          {rows.length > COLLAPSED_ROWS && (
            <button type="button" className="live-toggle" onClick={toggleExpanded} aria-expanded={expanded}>
              {expanded ? 'Свернуть' : `Показать все кабинеты (${rows.length})`}
              <svg className="live-toggle-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
        </>
      )}
    </section>
  );
}
